/** Shared pricing translation shape — imported by all locale files. */

import {
  WC2026_ORG_BILLING_DISCLOSURE,
  WC2026_ORG_BILLING_DISCLOSURE_SHORT,
} from '../../shared/constants/wc2026OrgBillingDisclosure.constants'

export const pricingTranslationsZh = {
  eyebrow: '套餐与定价',
  perMonth: '每月',
  perYear: '每年',
  copy: {
    pageTitle: '方案与定价',
    pageSubtitle:
      '一站式比较各方案。Hobby 对所有人免费。Pro 是个人订阅。Business 为 WC 2026 组织一次性费用（非 Pro）。Enterprise 需与我们的团队洽谈。',
    comingSoonBadge: '即将推出',
    comingSoonInProduction: '生产环境即将推出',
    mostPopularBadge: '最受欢迎',
    currentPlanBadge: '当前方案',
    createOrgCta: '创建组织',
    footnote:
      'Pro 向个人账户计费（无组织）。Business 为 WC 2026 组织一次性费用——创建组织、选择席位档位并一次性支付赛事期间组织访问权限。Enterprise 适用于需要定制定价、限额或合同的组织——请联系我们。',
    orgPlanDisclosure: WC2026_ORG_BILLING_DISCLOSURE,
    orgPlanDisclosureShort: WC2026_ORG_BILLING_DISCLOSURE_SHORT,
    footnoteCancelPolicy:
      '若在账单门户取消 Pro，Stripe 会将方案保持至已付费周期结束——在此之前可恢复。返回此处时，应用会与 Stripe 同步。',
    signInToSubscribe: '登录以订阅',
    logIn: '登录',
    goBack: '返回',
    manageSubscription: '管理订阅',
    proAccessEndsOn: 'Pro 访问将于以下日期结束',
    billingIntervalMonth: '月付',
    billingIntervalYear: '年付',
    switchToMonthly: '切换为月付',
    upgradeToYearlyDialogMsg:
      '年付为 12 个月承诺。切换后，在年付周期结束前无法改回月付——不提供部分退款。',
    upgradeToYearlyDialogConfirm: '切换为年付',
    upgradeToYearlyDialogCancel: '保持月付',
    switchToMonthlyDialogTitle: '切换为月付 Pro',
    switchToMonthlyDialogMsg:
      '将跳转至 Stripe 切换为月付。年付 Pro 访问将持续至当前周期结束——之后开始月付。未使用时间不予退款。',
    switchToMonthlyConfirm: '前往 Stripe',
    switchToMonthlyCancel: '保持年付',
    upgradeToYearlyProTitle: '升级至年付 Pro',
    upgradeToYearlyProConfirm: '升级',
    enterpriseSubscribe: '订阅',
    enterpriseCheckoutAdminOnly: '请组织管理员订阅',
    upgradeSeatBucket: '升级',
    managePlan: '方案与账单',
    businessSubscribe: '订阅',
    upgradeMonthlyToYearlyDialog: {
      line1: '您正从月付 Pro（{monthly}/month）切换为年付 Pro（{yearly}/year）。',
      line2: '现有支付方式将按当前账单周期收取按比例差额。',
      line3: '切换为年付后，在年付周期结束前无法改回月付。',
      line4: '继续？',
    },
  },
  plans: {
    hobby: {
      name: 'Hobby',
      priceLabel: '免费',
      tagline: '适合好友和小型群组入门。',
      ctaLabel: '开始使用',
      features: [
        '核心挑战与预测',
        '标准计分（无双倍）',
        '每个挑战的标准比赛行数',
        '标准支持',
        '偶尔广告',
        '适合与少数人一起体验 Zporti',
      ],
    },
    proMonthly: {
      name: 'Pro · 月付',
      tagline: '适合想要更多功能的个人玩家和好友群组。',
      ctaLabel: '按月订阅',
      features: [
        '包含 Hobby 全部功能',
        '启用双倍',
        '每个挑战更多比赛行数',
        '无广告体验',
        '更高的成员与活动限额',
        '优先支持',
        '更强的品牌定制选项',
      ],
    },
    proYearly: {
      name: 'Pro · 年付',
      tagline: '相同 Pro 功能，按年一次性计费——适合长期参与的群组。',
      ctaLabel: '按年订阅',
      features: [
        '包含 Hobby 全部功能',
        '启用双倍',
        '每个挑战更多比赛行数',
        '无广告体验',
        '更高的成员与活动限额',
        '优先支持',
        '更强的品牌定制选项',
      ],
    },
    business: {
      name: 'Business',
      priceLabel: 'From $450',
      tagline: 'WC 2026 组织一次性费用。选择席位档位并一次性支付赛事访问权限。',
      ctaLabel: '开始使用',
      features: [
        '组织品牌（颜色与 Logo）',
        '成员席位——固定档位最多 2,000',
        'WC 2026 赛事组织访问（品牌组织 + 关联球队）',
        '优先支持',
        '不含个人 Pro、金币、加成或应用内商店购买',
      ],
    },
    enterprise: {
      name: 'Enterprise',
      priceLabel: '定制',
      tagline: 'WC 2026 协商组织定价与推广——以订单或合同约定为准。',
      ctaLabel: '联系我们',
      features: [
        '组织品牌与推广（按协议）',
        '成员限额与销售团队协商',
        'WC 2026 及组织级功能（按协议）',
        '专属支持与定制入门引导',
        '定制集成、开票与合同',
        '不含个人 Pro、金币、加成或应用内商店购买',
      ],
    },
  },
  seatBuckets: {
    upTo100: '最多 100 名员工',
    upTo250: '最多 250 名员工',
    upTo500: '最多 500 名员工',
    upTo1000: '最多 1,000 名员工',
    upTo2000: '最多 2,000 名员工',
  },
} as const

export const featuresSectionZh = {
  eyebrow: '您将获得',
  livePreview: '实时预览',
  avatars: {
    title: '为每位玩家定制头像',
    description:
      '为每个人赋予可识别的身份，让排行榜、竞争关系和调侃从第一天起就更有个人感。',
    accessibilityLabel: '定制头像功能预览',
  },
} as const

export const footerExtendedZh = {
  product: '产品',
  compete: '竞技',
  company: '公司',
  phoneTagline: '与好友一起进行体育预测。',
  copyright: '© 2026 Zporti。由球迷制作，为球迷服务。',
  copyrightShort: '© 2026 Zporti',
} as const

export const organizationsZh = {
  heroBadge: '面向团队、联赛与品牌',
  heroTitle: 'Zporti 上的组织',
  heroLead:
    '在 Zporti 内以独立身份运营您的群组。设置品牌颜色与 Logo，通过加入码邀请成员，并在一处管理席位——让所有人一起游戏和预测时看到统一体验。',
  valueSectionTitle: '您将获得',
  valueBrandingTitle: '真正属于您的品牌',
  valueBrandingBody:
    '上传 Logo、选择主色与辅色，并预览在体验中的呈现方式。管理员无需编写代码即可保持视觉一致。',
  valueMembersTitle: '成员与席位',
  valueMembersBody:
    '创建组织或通过加入码加入。查看成员名单，按方案席位限额管理容量，并在接入新成员时复制加入码。',
  valueMascotTitle: '品牌吉祥物与头像',
  valueMascotBody:
    '可选的品牌吉祥物情绪与组织专属头像集帮助您的社区脱颖而出——适合球迷群、公司联赛与合作伙伴。',
  howSectionTitle: '如何运作',
  howStep1: '登录后创建组织，或使用管理员提供的邀请码加入。',
  howStep2: '管理员打开组织设置，调整品牌、成员与账户详情。',
  howStep3: '成员照常使用应用，在启用处看到您组织的视觉与权益。',
  signIn: '登录',
  signUp: '创建账户',
  heroPanelTitle: '组织控制中心',
  heroPanelBody: '邀请成员、设置品牌，并在开球前为每个群组提供统一、精致的主页。',
} as const

export const b2bContactZh = {
  title: '希望我们为您配置？',
  subtitle: '填写您的信息，我们的团队将联系您。',
  orgNameLabel: '组织名称 *',
  orgNamePlaceholder: '您的组织',
  contactNameLabel: '您的姓名 *',
  contactNamePlaceholder: '全名',
  emailLabel: '电子邮件 *',
  emailPlaceholder: 'you@example.com',
  messageLabel: '留言',
  messagePlaceholder: '告诉我们您的需求（可选）',
  submitButton: '发送请求',
  scheduleMemo: '或预约通话——即将推出',
  errorFallbackValidation: '请检查输入后重试。',
  errorFallbackSubmit: '提交失败，请重试。',
  successMessage: '谢谢！我们已收到您的请求，将尽快与您联系。',
} as const

export const signInZh = {
  signUpSubtitle: '注册以开始使用',
  signInSubtitle: '登录以继续',
  emailPlaceholder: '电子邮件',
  passwordPlaceholder: '密码',
  forgotPassword: '忘记密码？',
  signUpButton: '注册',
  signInButton: '登录',
  alreadyHaveAccount: '已有账户？',
  noAccount: '没有账户？',
  toggleSignIn: '登录',
  toggleSignUp: '注册',
  orDivider: '或',
  signInWith: '登录方式',
  continueAsGuest: '或以访客身份继续',
  legalConsentPrefix: '我已阅读并同意',
  legalConsentAnd: '和',
  legalConsentSuffix: '。',
  marketingConsent: '我同意接收 Zporti 的产品更新与营销邮件。',
  loading: '加载中…',
  sendingResetEmail: '正在发送重置邮件',
  signingUp: '正在注册',
  signingIn: '正在登录',
  continuingWithGoogle: '正在通过 Google 继续',
  continuingWithFacebook: '正在通过 Facebook 继续',
  enteringAsGuest: '正在以访客身份进入',
} as const

export const a11yZh = {
  openMenu: '打开菜单',
  closeMenu: '关闭菜单',
  heroMascot: 'Zporti 吉祥物手持手机',
} as const

export const accountAdminSiteZh = {
  backToSite: '← 返回网站',
} as const
